import {
	ApplicationInsights,
	ICustomProperties,
	IEventTelemetry,
	IExceptionTelemetry,
	ITraceTelemetry,
} from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';

/**
 * Singleton instances. Kept module-scoped so the same telemetry pipeline is
 * shared by the React provider, the axios interceptor and any ad-hoc caller.
 */
let appInsights: ApplicationInsights | null = null;
let reactPlugin: ReactPlugin | null = null;

const readConfigFromDom = (): { connectionString: string; cloudRole?: string } => {
	if (typeof document === 'undefined') {
		return { connectionString: '' };
	}
	const el = document.getElementById('app');
	return {
		connectionString: el?.dataset.appinsightsConnectionString ?? '',
		cloudRole: el?.dataset.appinsightsCloudRole ?? undefined,
	};
};

const resolveConnectionString = (explicit?: string): string => {
	if (explicit) return explicit;
	const fromDom = readConfigFromDom().connectionString;
	if (fromDom) return fromDom;
	// dotenv-webpack inlines process.env.* at build time
	return process.env.APPINSIGHTS_CONNECTION_STRING ?? '';
};

export interface InitAppInsightsOptions {
	/** Overrides the connection string discovered from DOM / env. */
	connectionString?: string;
	/** Logical name for this SPA in the App Insights application map. */
	cloudRoleName?: string;
	/** Backend origins that should receive W3C trace-context headers. */
	correlationHeaderDomains?: string[];
	/** Sampling percentage 0-100. Defaults to 100 (send all telemetry). */
	samplingPercentage?: number;
}

/**
 * Initializes Application Insights exactly once. Safe to call on every render;
 * subsequent calls return the cached instance. Returns null when no connection
 * string is configured so the app degrades gracefully in local/preview builds.
 */
export const initAppInsights = (options: InitAppInsightsOptions = {}): ApplicationInsights | null => {
	if (appInsights) return appInsights;

	const connectionString = resolveConnectionString(options.connectionString);
	if (!connectionString) {
		if (process.env.NODE_ENV !== 'production') {
			// eslint-disable-next-line no-console
			console.warn('[AppInsights] No connection string configured; telemetry disabled.');
		}
		return null;
	}

	const domConfig = readConfigFromDom();
	reactPlugin = new ReactPlugin();

	// Only inject W3C trace-context headers on cross-origin requests when the
	// caller supplied an explicit allowlist; otherwise CORS preflight failures
	// can occur against third-party endpoints that don't permit tracing headers.
	const corsAllowlist = options.correlationHeaderDomains?.filter((d) => !!d) ?? [];
	const enableCorsCorrelation = corsAllowlist.length > 0;

	appInsights = new ApplicationInsights({
		config: {
			connectionString,
			extensions: [reactPlugin],
			extensionConfig: {
				[reactPlugin.identifier]: {},
			},
			// SPA route + navigation instrumentation
			enableAutoRouteTracking: true,
			autoTrackPageVisitTime: true,
			// Dependency (XHR/fetch) tracking is on by default; keep it explicit.
			disableAjaxTracking: false,
			disableFetchTracking: false,
			// Correlate front-end requests with backend traces via W3C trace-context.
			distributedTracingMode: 2 /* AI_AND_W3C */,
			enableCorsCorrelation,
			correlationHeaderDomains: enableCorsCorrelation ? corsAllowlist : undefined,
			// Do not capture request/response bodies or headers by default (PII risk).
			enableRequestHeaderTracking: false,
			enableResponseHeaderTracking: false,
			// Capture unhandled exceptions from window.onerror / unhandledrejection.
			disableExceptionTracking: false,
			samplingPercentage: options.samplingPercentage ?? 100,
		},
	});

	appInsights.loadAppInsights();

	const cloudRoleName = options.cloudRoleName ?? domConfig.cloudRole;
	if (cloudRoleName) {
		appInsights.addTelemetryInitializer((envelope) => {
			// Must be an object (not an array) — AI serializes named properties
			// from `tags` into the envelope, which only works on plain objects.
			envelope.tags = envelope.tags ?? {};
			(envelope.tags as Record<string, string>)['ai.cloud.role'] = cloudRoleName;
		});
	}

	// Emit an initial pageview so single-page sessions are recorded even when
	// enableAutoRouteTracking fires late relative to first render.
	appInsights.trackPageView();

	return appInsights;
};

export const getAppInsights = (): ApplicationInsights | null => appInsights;

export const getReactPlugin = (): ReactPlugin | null => reactPlugin;

export const trackEvent = (event: IEventTelemetry, customProperties?: ICustomProperties): void => {
	appInsights?.trackEvent(event, customProperties);
};

export const trackException = (exception: IExceptionTelemetry, customProperties?: ICustomProperties): void => {
	appInsights?.trackException(exception, customProperties);
};

export const trackTrace = (trace: ITraceTelemetry, customProperties?: ICustomProperties): void => {
	appInsights?.trackTrace(trace, customProperties);
};

export { SeverityLevel } from '@microsoft/applicationinsights-web';
